import { CheckCircle, XCircle } from "@phosphor-icons/react"
import CustomCard from "components/Atoms/CustomCard"
import CustomTag from "components/Atoms/CustomTag"
import StyledLink from "components/Atoms/StyledLink"
import ReactTableHeader from "components/Common/ReactTableHeader"
import TablePagination from "components/Molecules/TablePagination"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import SerialColumn from "components/Organisms/table/SerialColumn"
import dayjs from "dayjs"
import { useAllCreditsQuery, useSummaryCreditsQuery } from "generated/graphql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import React, { useMemo } from "react"
import { Link } from "react-router-dom"
import ReactTable from "react-table"

const CREDIT_STATUS_MAP: Record<
  string,
  {
    label: string
    intent: "success" | "warning" | "danger" | "none" | "primary"
  }
> = {
  ACT: { label: "Active", intent: "success" },
  APR: { label: "Approved", intent: "primary" },
  EXP: { label: "Expired", intent: "none" },
  REJ: { label: "Rejected", intent: "danger" },
  REQ: { label: "Requested", intent: "warning" },
  SUS: { label: "Suspended", intent: "danger" },
}

export default function Credit() {
  const [{ page, search }, setFilter] = useSearchFilter()

  const { data, loading } = useAllCreditsQuery({
    variables: {
      query: search,
      first: 15,
      skip: page * 15,
    },
    fetchPolicy: "network-only",
  })

  const { data: summaryData } = useSummaryCreditsQuery({
    fetchPolicy: "network-only",
  })

  const summary = summaryData?.summaryCredits
  const credits = data?.allCredits

  const COLUMNS = useMemo(
    () => [
      {
        Header: "User Name",
        accessor: "user.fullName",
        width: 200,
        Cell: ({ value, original }) => {
          return (
            <Link to={`/d/registration/vendor-credit/${original.id}`}>
              {value}
            </Link>
          )
        },
      },
      {
        Header: "Total Amount",
        accessor: "totalAmount",
        Cell: ({ value }) => (
          <div className="flex justify-center w-full">
            <span className="text-green-300 font-bold">
              ₹{value?.toLocaleString() ?? 0}
            </span>
          </div>
        ),
      },
      {
        Header: "Used Amount",
        accessor: "usedAmount",
        Cell: ({ value }) => (
          <div className="flex justify-center w-full">
            <span className="text-red-500 font-bold ">
              ₹ {value?.toLocaleString() ?? 0}
            </span>
          </div>
        ),
      },
      {
        Header: "Remaining Amount",
        accessor: "remainingAmount",
        Cell: ({ value }) => (
          <div className="flex justify-center w-full">
            <span className="text-blue-400 font-bold">
              ₹ {value?.toLocaleString() ?? 0}
            </span>
          </div>
        ),
      },
      {
        Header: "Payment Cycle",
        accessor: "paymentCycleDays",
        Cell: ({ value }) => (
          <div className="flex justify-center w-full">
            <span className="text-brown-400 font-bold">{value} days</span>
          </div>
        ),
      },
      {
        Header: "Utilization %",
        accessor: "id",
        width: 160,
        Cell: ({ original }) => {
          const total = original.totalAmount || 1
          const used = original.usedAmount || 0
          const percentage = Math.round((used / total) * 100)
          return (
            <div className="flex flex-col gap-1 items-start justify-center w-2/3 mx-auto">
              <span className="text-[10px] font-bold">{percentage}%</span>
              <div className="w-full bg-blue-100 rounded-full h-1.5">
                <div
                  className="bg-[#006A4E] h-1.5 rounded-full"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>
          )
        },
      },
      {
        Header: "Start Date",
        accessor: "startDate",
        Cell: ({ value }) => (
          <div className="flex justify-center w-full">
            {value ? dayjs(value).format("DD MMM 'YY") : "--"}
          </div>
        ),
      },
      {
        Header: "End Date",
        accessor: "endDate",
        Cell: ({ value }) => (
          <div className="flex justify-center w-full">
            {value ? dayjs(value).format("DD MMM 'YY") : "--"}
          </div>
        ),
      },

      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => {
          const status = CREDIT_STATUS_MAP[value] || {
            label: value || "Unknown",
            intent: "none",
          }
          return (
            <div className="flex justify-center w-full">
              <CustomTag
                minimal
                intent={status.intent}
                className="w-20 text-center font-semibold flex justify-center"
              >
                {status.label}
              </CustomTag>
            </div>
          )
        },
      },
      {
        Header: "Is Active",
        accessor: "isActive",
        Cell: ({ value }) => {
          return (
            <div className="flex justify-center w-full">
              {value ? (
                <CheckCircle
                  size={20}
                  weight="fill"
                  className="text-green-300"
                />
              ) : (
                <XCircle size={20} weight="fill" className="text-red-500" />
              )}
            </div>
          )
        },
      },
      {
        Header: "Created At",
        accessor: "createdAt",
        width: 150,
        Cell: ({ value }) => (
          <div className="flex justify-center w-full">
            {value ? dayjs(value).format("DD MMMM 'YY | hh:mm a") : "--"}
          </div>
        ),
      },
      {
        Header: "",
        accessor: "actions",
        width: 10,
        Cell: () => {
          return <OverFlowMenu items={[]} />
        },
      },
    ],
    []
  )

  return (
    <div className="flex flex-col gap-4">
      {/* Summary Card */}

      <div className="w-full bg-white rounded-lg" style={{ borderRadius: 7 }}>
        <ReactTableHeader
          title="Vendor Credit"
          icon="credit-card"
          value={search}
          onQueryChange={v =>
            setFilter(prev => ({ ...prev, search: v, page: 0 }))
          }
        >
          <TablePagination
            className="mr-6"
            page={page}
            onPageChange={v => setFilter(prev => ({ ...prev, page: v }))}
          />
        </ReactTableHeader>
        <div
          className="flex items-center justify-between p-6 rounded-lg mx-20 bg-gray-100 mb-5"
          style={{ borderRadius: 7 }}
        >
          <div className="flex flex-col items-center">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
              Total Credit Sanctioned
            </span>
            <span className="text-xl font-bold text-[#006A4E] mt-2">
              ₹{summary?.totalCreditSanctioned?.toLocaleString() ?? "0"}
            </span>
          </div>

          <div className="h-10 w-[1.5px] bg-gray-300"></div>
          <div className="flex flex-col items-center">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
              Total Utilization
            </span>
            <span className="text-xl font-bold text-blue-500 mt-2">
              ₹ {summary?.totalUtilized?.toLocaleString() ?? "0"}
            </span>
          </div>
          <div className="h-10 w-[1.5px] bg-gray-300"></div>
          <div className="flex flex-col items-center">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
              Remaining Amount
            </span>
            <span className="text-xl font-bold text-red-500 mt-2">
              ₹ {summary?.remainingAmount?.toLocaleString() ?? "0"}
            </span>
          </div>
          <div className="h-10 w-[1.5px] bg-gray-300"></div>
          <div className="flex flex-col items-center">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
              Active Users
            </span>
            <span className="text-xl font-bold text-lime-500 mt-2">
              {summary?.activeUsers ?? "0"}
            </span>
          </div>
        </div>
        <ReactTable
          loading={loading}
          manual
          sortable={false}
          showPagination={false}
          pages={15}
          page={page}
          pageSize={15}
          className="mt-0 text-center vt-master-table"
          resizable={true}
          data={credits || []}
          columns={COLUMNS}
        />
      </div>
    </div>
  )
}
